import * as React from 'react';

import { Binding, Result } from 'core/infrastructure';
import { Convert } from 'core/utils';
import ErrorBlock from './ErrorBlock';
import { FormComponent } from './Form';
import { Colors } from '../graphics';
import { formatAsElementId } from './Utils';
import { showCustom } from './Dialog';
import Button from './Button';
import { StyleButtonImagePickerDialog } from './StyleButtonImagePickerDialog';
import type { StyleButtonMask } from './StyleButtonImagePickerTypes';

export type StyleButtonType = 'Avatar' | 'ImageText' | 'TextOnly' | 'Legacy';

interface Props<T> extends React.HTMLProps<StyleButton<T>> {
	formId?: string;
	linkTo: Binding.PropertyPath<T>;
	label?: string;
	boundValue?: string;
	type: StyleButtonType;
	initialText: string;
	initialRgbColor: string;
}

interface State {
	imageContentAsBase64: string;
	errorMsg: string;
	text: string;
	rgbColor: string;
}

function useImageSrcUrlFromBase64(base64?: string | null): string {
	const [src, setSrc] = React.useState('');

	React.useEffect(() => {
		if (!base64) { setSrc(''); return; }
		const blob = Convert.base64StringToBlob(base64);
		if (!blob || !blob.size) { setSrc(''); return; }

		const url = URL.createObjectURL(blob);
		setSrc(url);
		return () => URL.revokeObjectURL(url);
	}, [base64]);

	return src;
}

function getMaskByType(type: StyleButtonType): StyleButtonMask {

	// width y height son los tamaños _reales_ que se usarán al final para guardar
	// la imagen. En general, deben ser al menos el doble de los tamaños que se usarán
	// al mostrar la imagen en pantalla para que no se vean borrosos en las pantallas
	// retina. PreviewWidth es el tamaño que se usará en pantalla para editar la imagen

	switch (type) {
		case 'Legacy':
			return { width: 128, height: 128, borderRadiusPx: 0, previewWidth: 256 };
		case 'ImageText':
			return { width: 200, height: 112, borderRadiusPx: 8, previewWidth: 400 };
		case 'Avatar':
			return { width: 200, height: 200, borderRadiusPx: 100, previewWidth: 400 };
		case 'TextOnly':
			return { width: 0, height: 0, borderRadiusPx: 0, previewWidth: 0 };
	}
}

function LegacyStyleButtonControl(params: {
	id: string;
	text: string;
	rgbColor: string;
	imageContentAsBase64: string;
	onOpenImagePicker: () => void;
	onClearImage: () => void;
}): React.ReactNode {
	const imageSrc = useImageSrcUrlFromBase64(params.imageContentAsBase64);
	const image = imageSrc ? <img className='needsclick' src={imageSrc} /> : <img style={{ display: 'none' }} />;
	const spanStyle: React.CSSProperties = imageSrc
		? { color: 'black' }
		: { backgroundColor: Colors.rgbToHex(params.rgbColor), color: Colors.getTextColor(params.rgbColor), wordBreak: 'break-word' };

	return <div style={{ display: 'flex' }}>
		<div id={params.id} className='btn-style needsclick' style={{ cursor: 'default' }}>
			{image}
			<span style={spanStyle}>{params.text}</span>
		</div>
		<span className='m-l-sm' style={{ verticalAlign: 'top' }}>
			<span className='m-l-xs'>
				<Button onClick={params.onOpenImagePicker}><i className='text-info fa fa-image'></i></Button>
			</span>
			<span className='m-l-xs'>
				<Button onClick={params.onClearImage}><i className='text-info fa fa-remove'></i></Button>
			</span>
		</span>
	</div>;
}

function ImageTextStyleButtonControl(params: {
	id: string;
	text: string;
	rgbColor: string;
	imageContentAsBase64: string;
	onOpenImagePicker: () => void;
	onClearImage: () => void;
}): React.ReactNode {
	const imageSrc = useImageSrcUrlFromBase64(params.imageContentAsBase64);
	const backgroundColor = Colors.rgbToHex(params.rgbColor);
	const textColor = Colors.getTextColor(params.rgbColor);

	const image = imageSrc
		? <img className='image-text-button-image needsclick' src={imageSrc} />
		: null;

	const text = params.text
		? <span className='image-text-button-label' style={{ color: textColor }}>{params.text}</span>
		: null;

	return <div style={{ display: 'flex' }}>
		<div id={params.id} style={{ backgroundColor, cursor: 'default' }} className='image-text-button needsclick'>
			{image}
			{text}
		</div>
		<span className='m-l-sm' style={{ verticalAlign: 'top' }}>
			<span className='m-l-xs'>
				<Button onClick={params.onOpenImagePicker}><i className='text-info fa fa-image'></i></Button>
			</span>
			<span className='m-l-xs'>
				<Button onClick={params.onClearImage}><i className='text-info fa fa-remove'></i></Button>
			</span>
		</span>
	</div>;
}

function AvatarStyleButtonControl(params: {
	id: string;
	text: string;
	rgbColor: string;
	imageContentAsBase64: string;
	onOpenImagePicker: () => void;
	onClearImage: () => void;
}): React.ReactNode {
	const imageSrc = useImageSrcUrlFromBase64(params.imageContentAsBase64);
	const backgroundColor = Colors.rgbToHex(params.rgbColor);
	const textColor = Colors.getTextColor(params.rgbColor);

	const image = imageSrc
		? <img className='avatar-button-image needsclick' src={imageSrc} />
		: <div className='avatar-button-text needsclick' style={{ backgroundColor, color: textColor }}>
			{params.text.toUpperCase().replace(/[^a-zA-ZÁÉÍÓÚÑÜ]/g, '').slice(0, 2)}
		</div>;

	return <div style={{ display: 'flex' }}>
		<div id={params.id} className='avatar-button needsclick' style={{ cursor: 'default' }}>
			{image}
			<div className='avatar-button-label'>{params.text}</div>
		</div>
		<span className='m-l-sm' style={{ verticalAlign: 'top' }}>
			<span className='m-l-xs'>
				<Button onClick={params.onOpenImagePicker}><i className='text-info fa fa-image'></i></Button>
			</span>
			<span className='m-l-xs'>
				<Button onClick={params.onClearImage}><i className='text-info fa fa-remove'></i></Button>
			</span>
		</span>
	</div>;
}

function TextOnlyStyleButtonControl(params: {
	id: string;
	text: string;
	rgbColor: string;
}): React.ReactNode {
	const backgroundColor = Colors.rgbToHex(params.rgbColor);
	const textColor = Colors.getTextColor(params.rgbColor);

	return <div id={params.id} className='text-only-button needsclick' style={{ backgroundColor, cursor: 'default' }}>
		<div style={{ color: textColor }}>{params.text}</div>
	</div>;
}

export class StyleButton<T> extends React.Component<Props<T>, State> implements FormComponent {
	private ref = React.createRef<HTMLDivElement>();

	constructor(props: Props<T>) {
		super(props);

		if (typeof props.boundValue !== 'string')
			throw new Error('Image selector esperaba una imagen en formato base64 string');

		this.state = {
			imageContentAsBase64: props.boundValue || '',
			text: props.initialText,
			rgbColor: props.initialRgbColor || '#bacde2',
			errorMsg: ''
		};

		this.openImagePicker = this.openImagePicker.bind(this);
		this.clearImage = this.clearImage.bind(this);
	}

	componentDidMount() {
		$('*', this.ref.current!).addClass('needsclick');
	}

	getPath(): string {
		return Binding.propertyPathToString(this.props.linkTo);
	}

	getValue(): Result<string> {
		this.setState({ errorMsg: '' });
		return Result.ok(this.state.imageContentAsBase64);
	}

	showErrors(errors: string[]): void {
		this.setState({ errorMsg: errors.join(' ') });
	}

	private clearImage() {
		this.setState({ imageContentAsBase64: '' });
	}

	private async openImagePicker() {
		const mask = getMaskByType(this.props.type);
		const backgroundColor = Colors.rgbToHex(this.state.rgbColor);
		const result = await showCustom<string>(<StyleButtonImagePickerDialog
			mask={mask}
			backgroundColor={backgroundColor}
			initialImageBase64={this.state.imageContentAsBase64}
		/>);

		if (result.ok)
			this.setState({ imageContentAsBase64: result.value });
	}

	renderStyleButtonControl(type: StyleButtonType): React.ReactNode {
		const id = formatAsElementId(this.getPath(), this.props.formId || '');
		const imageParams = {
			id,
			text: this.state.text,
			rgbColor: this.state.rgbColor,
			imageContentAsBase64: this.state.imageContentAsBase64,
			onOpenImagePicker: this.openImagePicker,
			onClearImage: this.clearImage,
		};

		switch (type) {
			case 'Legacy': return <LegacyStyleButtonControl {...imageParams} />;
			case 'Avatar': return <AvatarStyleButtonControl {...imageParams} />;
			case 'ImageText': return <ImageTextStyleButtonControl {...imageParams} />;
			case 'TextOnly': return <TextOnlyStyleButtonControl id={id} text={this.state.text} rgbColor={this.state.rgbColor} />;
		}
	}

	render() {
		const label = this.props.label ? <label className='col-sm-4 control-label'>{this.props.label}</label> : null;

		return <div className='form-group' ref={this.ref}>
			{label}
			<div className={this.props.label ? 'col-sm-8' : 'col-sm-12'}>
				{this.renderStyleButtonControl(this.props.type)}
			</div>
			<ErrorBlock msg={this.state.errorMsg} />
		</div>;
	}
}

export type StyleButtonCtor<T> = new (props: Props<T>) => React.Component<Props<T>, State>;
export type { StyleButtonMask };
